/**
 * Custom Login Page
 *
 * Admin panel login with multiple authentication methods and MFA support.
 *
 * Authentication Methods:
 * - Email/Password (Cognito)
 * - Google OAuth (Sign in with Google)
 * - Apple OAuth (Sign in with Apple)
 *
 * MFA Flow (Multi-Factor Authentication):
 * 1. User enters credentials
 * 2. If MFA enabled (requiresMfa=true): Show MfaVerify dialog for TOTP code
 * 3. If MFA setup forced (requiresMfaSetup=true): Show MfaSetup dialog to scan QR code
 *    - This happens when admin enabled "Require MFA" but user hasn't set it up yet
 * 4. After MFA verification/setup: Redirect to dashboard
 *
 * State Variables:
 * - mfaRequired: User has MFA enabled, needs to enter TOTP code
 * - mfaSetupRequired: Admin forced MFA, user must set it up before proceeding
 * - mfaSession: Cognito session token for MFA challenge
 *
 * @see MfaVerify - Dialog for entering 6-digit TOTP code
 * @see MfaSetup - Dialog for scanning QR code and enabling MFA
 * @see GoogleLoginButton - Google OAuth button component
 * @see AppleLoginButton - Apple OAuth button component
 */

import { useState } from 'react';
import {
  Login,
  useLogin,
  useNotify,
  Form,
  TextInput,
  required,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  alpha,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  LocalShipping as DeliveryIcon,
} from '@mui/icons-material';
import { GoogleLoginButton } from './GoogleLoginButton';
import { AppleLoginButton } from './AppleLoginButton';
import { MfaVerify } from './MfaVerify';
import { MfaSetup } from './MfaSetup';
import config from '../config';
import Logger from '../utils/Logger';

/**
 * Custom Login Page with Google and Apple OAuth + MFA support
 */
const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSession, setMfaSession] = useState('');
  const [mfaUsername, setMfaUsername] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const login = useLogin();
  const notify = useNotify();

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setMfaError('');

    try {
      const response = await fetch(`${config.API_URL}/auth/sing-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.username,
          password: values.password,
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid email or password');
      }

      const data = await response.json();

      if (data.requiresMfa) {
        Logger.log('[LoginPage] MFA required');
        setMfaRequired(true);
        setMfaSession(data.session);
        setMfaUsername(values.username);
        return;
      }

      localStorage.setItem('token', data.idToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }
      if (data.deviceKey) {
        localStorage.setItem('deviceKey', data.deviceKey);
      }
      if (data.roles) {
        localStorage.setItem('userRoles', JSON.stringify(data.roles));
      }

      if (data.requiresMfaSetup) {
        Logger.log('[LoginPage] MFA setup required');
        notify('Please set up two-factor authentication to continue', { type: 'info' });
        setMfaSetupRequired(true);
        return;
      }

      const storedRoles = localStorage.getItem('userRoles');
      const roles = storedRoles ? JSON.parse(storedRoles) : [];
      const redirectPath = '/';
      window.location.href = redirectPath;
    } catch (error) {
      Logger.error('[LoginPage] Login error:', error);
      notify('Invalid email or password', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (code: string) => {
    setMfaError('');

    try {
      const response = await fetch(`${config.API_URL}/auth/mfa/challenge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session: mfaSession,
          code: code,
          username: mfaUsername,
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid verification code');
      }

      const data = await response.json();

      localStorage.setItem('token', data.idToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }
      if (data.deviceKey) {
        localStorage.setItem('deviceKey', data.deviceKey);
      }

      notify('Signed in successfully', { type: 'success' });

      const storedRoles = localStorage.getItem('userRoles');
      const roles = storedRoles ? JSON.parse(storedRoles) : [];
      const redirectPath = '/';
      window.location.href = redirectPath;
    } catch (error: any) {
      Logger.error('[LoginPage] MFA verification error:', error);
      setMfaError(error.message || 'Invalid code. Please try again.');
      throw error;
    }
  };

  const handleMfaCancel = () => {
    setMfaRequired(false);
    setMfaSession('');
    setMfaUsername('');
    setMfaError('');
  };

  const handleMfaSetupRequired = () => {
    setMfaSetupRequired(true);
  };

  const handleMfaSetupSuccess = () => {
    setMfaSetupRequired(false);
    notify('MFA set up successfully! You can now access the admin panel.', { type: 'success' });
    const storedRoles = localStorage.getItem('userRoles');
    const roles = storedRoles ? JSON.parse(storedRoles) : [];
    const redirectPath = '/';
    window.location.href = redirectPath;
  };

  const handleMfaSetupCancel = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('deviceKey');
    setMfaSetupRequired(false);
    notify('MFA setup is required to access the admin panel', { type: 'warning' });
  };

  const isGoogleConfigured = Boolean(config.GOOGLE_CLIENT_ID);
  const isAppleConfigured = Boolean(config.APPLE_CLIENT_ID);
  const hasOAuthOptions = isGoogleConfigured || isAppleConfigured;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: `
          linear-gradient(135deg, #FF1744 0%, #D50000 50%, #B71C1C 100%)
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 40%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Floating decoration elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          opacity: 0.1,
          transform: 'rotate(-15deg)',
        }}
      >
        <DeliveryIcon sx={{ fontSize: 120, color: 'white' }} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '8%',
          opacity: 0.08,
          transform: 'rotate(20deg)',
        }}
      >
        <DeliveryIcon sx={{ fontSize: 150, color: 'white' }} />
      </Box>

      {/* Main Card */}
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          mx: 2,
          borderRadius: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 40px rgba(255,23,68,0.2)',
          overflow: 'visible',
          position: 'relative',
          backdropFilter: 'blur(10px)',
          background: 'rgba(255,255,255,0.98)',
        }}
      >
        {/* Logo floating above card */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 100,
            height: 100,
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            border: '4px solid white',
          }}
        >
          <Box
            component="img"
            src="/deeliver-logo.png"
            alt="Deeliver"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>

        <CardContent sx={{ pt: 8, pb: 4, px: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                color: '#1a1a1a',
                letterSpacing: '-0.5px',
                mb: 0.5,
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '0.95rem',
              }}
            >
              Sign in to the Admin Panel
            </Typography>
          </Box>

          {/* Login Form */}
          <Form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2.5 }}>
              <TextInput
                source="username"
                label="Email Address"
                fullWidth
                validate={required()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#FF1744', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#f0f1f2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF1744',
                        borderWidth: 2,
                      },
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#1a1a1a',
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextInput
                source="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                validate={required()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#FF1744', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#f0f1f2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF1744',
                        borderWidth: 2,
                      },
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#1a1a1a',
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.6,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #FF1744 0%, #D50000 100%)',
                boxShadow: '0 4px 15px rgba(255,23,68,0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF4569 0%, #FF1744 100%)',
                  boxShadow: '0 6px 20px rgba(255,23,68,0.5)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&.Mui-disabled': {
                  background: '#ccc',
                  boxShadow: 'none',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </Form>

          {/* OAuth Section */}
          {hasOAuthOptions && (
            <>
              <Box sx={{ my: 3 }}>
                <Divider>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      px: 2,
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    or continue with
                  </Typography>
                </Divider>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {isGoogleConfigured && <GoogleLoginButton onMfaSetupRequired={handleMfaSetupRequired} />}
                {isAppleConfigured && <AppleLoginButton onMfaSetupRequired={handleMfaSetupRequired} />}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <Box
        sx={{
          mt: 4,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          Deeliver Admin Panel
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          Powered by Deeliver
        </Typography>
      </Box>

      {/* MFA Dialogs */}
      <MfaVerify
        open={mfaRequired}
        session={mfaSession}
        username={mfaUsername}
        onVerify={handleMfaVerify}
        onCancel={handleMfaCancel}
        error={mfaError}
      />

      <MfaSetup
        open={mfaSetupRequired}
        onClose={handleMfaSetupCancel}
        onSuccess={handleMfaSetupSuccess}
      />
    </Box>
  );
};

export default LoginPage;
